import React, {createContext, useContext, useEffect, useState} from 'react';
import {Provider} from 'react-redux';
import {BrowserRouter} from 'react-router-dom';
import config from '@ey/voltron-config';
import {merge} from 'lodash';
import configureStore from '../store/configurestore';
import initialState from '../reducers/initialstate';
import FirstLoadOverlay from './Common/FirstLoadOverlay/FirstLoadOverlay';

export const EventContext = createContext();
export const EventProvider = EventContext.Provider;

export const HostContext = createContext();
export const HostProvider = HostContext.Provider;

export default function EntryPoint(Component, {enableRouter = false} = {}) {
	return function $EntryPoint({
		telemetry = true,
		adConfig,
		runtimeErrorFallback,
		onRuntimeError,
		basename = '',
		eventContextDispatch = reactEventDispatch,
		preloadedState = {},
		...props
	}) {
		return (
			<EventProvider value={eventContextDispatch(props)}>
				<Startup
					adConfig={adConfig}
					telemetry={telemetry}
					runtimeErrorFallback={runtimeErrorFallback}
					onRuntimeError={onRuntimeError}
					basename={basename}
					enableRouter={enableRouter}
					preloadedState={preloadedState}
					path={props.path}
				>
					<Component {...props} preloadedState={preloadedState} basename={basename} />
				</Startup>
			</EventProvider>
		);
	};
}

function Startup({children, adConfig, basename = '/', enableRouter, preloadedState = {}}) {
	const [ready, setReady] = useState(false);
	const [store, setStore] = useState(null);

	useEffect(() => {
		if (adConfig) {
			config.ad = merge(config.ad, adConfig);
			if (adConfig.scopes) {
				config.ad.scopes = adConfig.scopes;
			}
		}

		// Deep merge initialState with preloadedState to properly initialize Redux store
		const mergedInitialState = merge({}, initialState, preloadedState);

		const store = configureStore(mergedInitialState, config);
		store.dispatch({type: 'HOME_LOGIN'}).then(() => setReady(true));
		setStore(store);
	}, []);

	return ready ? (
		<HostProvider value={{basename}}>
			<Provider store={store}>{enableRouter ? <BrowserRouter basename={basename}>{children}</BrowserRouter> : children}</Provider>
		</HostProvider>
	) : (
		<FirstLoadOverlay />
	);
}

function reactEventDispatch(props) {
	return function $reactEventDispatch(eventName, arg) {
		const eventPropName = [`on${eventName.charAt(0).toUpperCase() + eventName.slice(1)}`, `on${eventName}`, eventName].filter((n) => typeof props[n])[0];

		if (eventPropName) {
			props[eventPropName](arg);
		}
	};
}

export function useEventDispatch() {
	return useContext(EventContext);
}
